/**
 * The voice orb for the Game Coder page.
 *
 * Story Builder's `VoiceOrb` can't be reused directly: it reads `socketStore`'s
 * voice state and calls its mute/unmute actions, which drive Story Builder's
 * session. This wrapper renders the same WebGL orb HTML but binds press and
 * audio-level state to `coderStore` instead.
 *
 * The renderer itself (shaders, faces, animation) is shared — `voiceorbHtml` is
 * a build-time asset, not Story Builder logic, so pointing at it is reuse rather
 * than coupling.
 */

import React, { useEffect, useRef } from "react";
import { View, Pressable, Text, StyleSheet, Platform } from "react-native";
import { useAudioStore } from "../store/audioStore";
import { colors } from "../styles";
import voiceorbHtml from "../components/voice/voiceorbHtml";
import { useCoderStore } from "./coderStore";

interface CoderOrbProps {
  size?: number;
}

export default function CoderOrb({ size = 160 }: CoderOrbProps) {
  const isVoiceActive = useCoderStore((s) => s.isVoiceActive);
  const startVoice = useCoderStore((s) => s.startVoice);
  const stopVoice = useCoderStore((s) => s.stopVoice);

  const handlePress = () => {
    if (isVoiceActive) stopVoice();
    else startVoice();
  };

  const a11yLabel = isVoiceActive ? "Stop talking to Bit" : "Start talking to Bit";

  if (Platform.OS === "web") {
    return (
      <Pressable
        onPress={handlePress}
        testID="coder-orb"
        accessibilityLabel={a11yLabel}
        accessibilityRole="button"
        style={{ cursor: "pointer" } as never}
      >
        <WebCoderOrb size={size} />
      </Pressable>
    );
  }

  // Native fallback — the Android ViewManager path is Story Builder-specific
  // wiring we don't need for the hackathon web target.
  return (
    <Pressable
      onPress={handlePress}
      testID="coder-orb"
      accessibilityLabel={a11yLabel}
      accessibilityRole="button"
      style={[styles.fallback, { width: size, height: size, borderRadius: size / 2 }]}
    >
      <Text style={styles.fallbackIcon}>{isVoiceActive ? "■" : "🎤"}</Text>
    </Pressable>
  );
}

/**
 * Web: the orb renders in a `srcdoc` iframe and is fed mic + playback levels
 * every frame via postMessage. targetOrigin is "*" because a srcdoc iframe has
 * origin "null"; safe here since we author the document.
 */
function WebCoderOrb({ size }: { size: number }) {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    let rafId = 0;
    const tick = () => {
      rafId = requestAnimationFrame(tick);
      const { audioPlayer, audioProcessor } = useAudioStore.getState();
      const { isVoiceActive, isThinking } = useCoderStore.getState();
      iframeRef.current?.contentWindow?.postMessage(
        {
          type: "orbState",
          audioLevel: audioPlayer?.getAudioLevel() ?? 0,
          micLevel: audioProcessor?.getMicLevel() ?? 0,
          isActive: isVoiceActive,
          isConnected: true,
          orbMode: isVoiceActive ? "active" : "inactive",
        },
        "*",
      );
      iframeRef.current?.contentWindow?.postMessage(
        // Face variants come from the shared orb HTML: 05 idle-inactive,
        // 07 thinking, 01 listening.
        { type: "setFace", variant: !isVoiceActive ? "05" : isThinking ? "07" : "01" },
        "*",
      );
    };
    tick();
    return () => cancelAnimationFrame(rafId);
  }, []);

  if (!voiceorbHtml) return null;

  // The orb's glow overflows its logical bounds, so the iframe is drawn at 2x
  // and centred with a negative offset.
  const iframeSize = Math.round(size * 2);
  const offset = Math.round((iframeSize - size) / 2);

  return (
    <View style={{ width: size, height: size, position: "relative" }}>
      <iframe
        ref={iframeRef}
        srcDoc={voiceorbHtml}
        title="Bit Voice Orb"
        style={{
          width: iframeSize,
          height: iframeSize,
          border: "none",
          background: "transparent",
          display: "block",
          pointerEvents: "none",
          position: "absolute",
          top: -offset,
          left: -offset,
        }}
        // @ts-expect-error — not in React's typings, but browsers honour it.
        allowtransparency="true"
      />
    </View>
  );
}

const styles = StyleSheet.create({
  fallback: {
    backgroundColor: colors.accent,
    alignItems: "center",
    justifyContent: "center",
  },
  fallbackIcon: {
    color: colors.white,
    fontSize: 28,
  },
});
